import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Linkedin, Send, Loader2 } from 'lucide-react';
import { profile } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import BackToTop from './BackToTop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: '', email: '', subject: '', message: '' };

const validateForm = (form) => {
  if (!form.name || !form.email || !form.message) {
    return { ok: false, reason: 'Please share your name, email and message.' };
  }
  return { ok: true };
};

const extractErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return 'Something went wrong sending your message. Please try again or email directly.';
};

const submitContactMessage = (payload) =>
  axios.post(`${API}/contact`, payload, { headers: { 'Content-Type': 'application/json' } });

const ContactInfoItem = ({ icon: Icon, label, value, href, external }) => {
  const content = (
    <>
      <div className="w-11 h-11 border border-white/15 group-hover:border-[#c9a961] flex items-center justify-center transition-colors">
        <Icon size={17} className="text-[#c9a961]" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{label}</div>
        <div className="text-white group-hover:text-[#c9a961] transition-colors">{value}</div>
      </div>
    </>
  );
  if (!href) {
    return <div className="flex items-center gap-4">{content}</div>;
  }
  const extraProps = external ? { target: '_blank', rel: 'noreferrer' } : {};
  return (
    <a href={href} className="flex items-center gap-4 group" {...extraProps}>
      {content}
    </a>
  );
};

const ContactInfo = () => (
  <div className="lg:col-span-5">
    <div className="section-eyebrow">Get in touch</div>
    <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
      Let’s build something
      <span className="italic text-[#c9a961]"> excellent</span>.
    </h2>
    <div className="gold-line mt-6" />
    <p className="text-white/70 mt-6 leading-relaxed max-w-md">
      Open to senior QA leadership, business analysis and program delivery roles in financial
      services. Happy to discuss consulting, mentorship or speaking engagements.
    </p>
    <div className="mt-10 space-y-5">
      <ContactInfoItem
        icon={Mail}
        label="Email"
        value={profile.email}
        href={`mailto:${profile.email}`}
      />
      <ContactInfoItem
        icon={Phone}
        label="Phone"
        value={profile.phone}
        href={`tel:${profile.phone.replace(/[^+\d]/g, '')}`}
      />
      <ContactInfoItem icon={MapPin} label="Based in" value={profile.location} />
      <ContactInfoItem
        icon={Linkedin}
        label="LinkedIn"
        value="/in/vigneshsofficial"
        href={profile.linkedin}
        external
      />
    </div>
  </div>
);

const FormField = ({ id, name, label, type = 'text', value, onChange, placeholder, textarea, rows }) => {
  const className =
    'mt-2 bg-transparent border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#c9a961] focus-visible:border-[#c9a961]';
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-[11px] tracking-[0.28em] uppercase text-white/55"
      >
        {label}
      </Label>
      {textarea ? (
        <Textarea
          id={id}
          name={name}
          rows={rows || 6}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
        />
      ) : (
        <Input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
        />
      )}
    </div>
  );
};

const ContactForm = ({ form, submitting, onChange, onSubmit }) => (
  <div className="lg:col-span-7">
    <form onSubmit={onSubmit} className="bg-[#07111f] border border-white/10 p-7 md:p-10">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField
          id="name"
          name="name"
          label="Name"
          value={form.name}
          onChange={onChange}
          placeholder="Your full name"
        />
        <FormField
          id="email"
          name="email"
          label="Email"
          type="email"
          value={form.email}
          onChange={onChange}
          placeholder="you@company.com"
        />
      </div>
      <div className="mt-5">
        <FormField
          id="subject"
          name="subject"
          label="Subject"
          value={form.subject}
          onChange={onChange}
          placeholder="How can I help?"
        />
      </div>
      <div className="mt-5">
        <FormField
          id="message"
          name="message"
          label="Message"
          value={form.message}
          onChange={onChange}
          placeholder="Share a few details about the role, project or initiative…"
          textarea
        />
      </div>
      <div className="mt-7 flex items-center justify-between">
        <p className="text-xs text-white/40 max-w-sm">
          Your details are kept confidential and used only to respond to your enquiry.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-70"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  </div>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const { ok, reason } = validateForm(form);
    if (!ok) {
      toast({ title: 'Missing details', description: reason });
      return;
    }
    setSubmitting(true);
    try {
      await submitContactMessage(form);
      toast({ title: 'Message received', description: "Thank you. I'll be in touch shortly." });
      setForm(initialForm);
    } catch (err) {
      toast({ title: 'Unable to send', description: extractErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0c1a2b] text-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <ContactInfo />
        <ContactForm form={form} submitting={submitting} onChange={onChange} onSubmit={onSubmit} />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 flex flex-col sm:flex-row items-center justify-center gap-6">
        <BackToTop targetId="contact" label="Top of Contact" variant="dark" />
        <span className="hidden sm:inline-block w-px h-5 bg-white/15" />
        <BackToTop targetId="top" label="Top of Page" variant="dark" />
      </div>
    </section>
  );
};

export default Contact;
